/**
 * O status de um feedback: consultar, trocar, ver o histórico e exportar.
 *
 * **A troca é otimista, e a tela precisa participar disso.** Quem troca manda
 * também o status que a tela está mostrando (`codigoEsperado`); se o feedback já
 * não estiver nele, a API responde **409** com `codigoAtual` e `nomeAtual` no
 * corpo. Sem isso, duas pessoas com a mesma lista aberta sobrescrevem uma à
 * outra e o histórico registra uma troca que ninguém viu acontecer.
 *
 * **Feedback sem linha de status vale `todo`.** São 18 dos 73 em produção — a
 * API resolve isso, e é por isso que a lista nunca mostra status em branco.
 */

import { api, foraDoProjeto, obter } from "./cliente"
import { ErroDaApi } from "./erros"

export type StatusDeFeedback = { codigo: string; nome: string }

export type TrocaDeStatus = {
  id: number
  deCodigo: string
  de: string
  paraCodigo: string
  para: string
  quando: string
  painelId: number
  administrador: string
}

export type HistoricoDeStatus = {
  feedbackId: number
  statusAtualCodigo: string
  statusAtual: string
  trocas: TrocaDeStatus[]
}

export type ResultadoDaTroca = {
  resultado: "atualizado" | "sem_alteracao"
  codigo: string
  nome: string
  mensagem: string
}

/** O que o 409 carrega, para a tela se corrigir sem outra viagem. */
export type ConflitoDeStatus = { codigoAtual: string; nomeAtual: string }

export function listarStatusDeFeedback() {
  return obter<StatusDeFeedback[]>("api/relatorios/feedback/status", {})
}

export function historicoDeFeedback(feedbackId: number) {
  return obter<HistoricoDeStatus>(`api/relatorios/feedback/${feedbackId}/historico`, {})
}

export async function trocarStatusDeFeedback(
  feedbackId: number,
  codigo: string,
  codigoEsperado: string,
) {
  const r = await api.put<ResultadoDaTroca>(
    `api/relatorios/feedback/${feedbackId}/status`,
    { codigo, codigoEsperado },
    foraDoProjeto(),
  )
  return r.data
}

/** O `codigoAtual` do 409, quando houver. */
export function conflitoDe(e: unknown): ConflitoDeStatus | null {
  if (!(e instanceof ErroDaApi) || e.status !== 409) return null
  const p = e.problema as Record<string, unknown> | null
  if (!p) return null
  const codigo = String(p.codigoAtual ?? "")
  return codigo ? { codigoAtual: codigo, nomeAtual: String(p.nomeAtual ?? codigo) } : null
}

/**
 * Uma foto de feedback de cliente, como URL de blob para um `<img>`.
 *
 * O relatório traz só o nome do arquivo; a foto vem da ApiSistema, que a lê da
 * ApiConteudo. Vem pelo axios porque o `<img src>` não manda o token. Quem chama
 * revoga a URL quando o componente sai.
 */
export async function urlDaImagemDeFeedback(nome: string, largura?: number) {
  const r = await api.get<Blob>(`api/relatorios/feedback/imagens/${encodeURIComponent(nome)}`, {
    params: { largura },
    responseType: "blob",
  })
  return URL.createObjectURL(r.data)
}

export type TipoDeFeedback = { codigo: string; nome: string }

/**
 * Os tipos que o botão do site oferece — hoje três: gostei, não gostei e
 * "tenho uma ideia".
 *
 * Vem da API e não de uma lista aqui porque é a mesma decisão do catálogo de
 * status: o dia em que nascer um quarto tipo, a tela o mostra sem alteração.
 */
export function listarTiposDeFeedback() {
  return obter<TipoDeFeedback[]>("api/relatorios/feedback/tipos", {})
}

export type FiltroDeFeedback = {
  dataInicio: string
  dataFim: string
  tipoCodigos?: string[]
  statusCodigos?: string[]
  filtroImagens?: "todos" | "com" | "sem"
}

/**
 * O CSV do período. Sai com **BOM** — sem ele o Excel abre em ANSI e todo
 * acento vira lixo — e com proteção contra fórmula: a mensagem é escrita pelo
 * cliente, e uma que comece com `=` viraria fórmula executada em quem abrir.
 */
export async function baixarCsvDeFeedback(f: FiltroDeFeedback) {
  const p = new URLSearchParams()
  p.set("dataInicio", f.dataInicio)
  p.set("dataFim", f.dataFim)
  for (const t of f.tipoCodigos ?? []) p.append("tipoCodigos", t)
  for (const s of f.statusCodigos ?? []) p.append("statusCodigos", s)
  if (f.filtroImagens) p.set("filtroImagens", f.filtroImagens)

  const r = await api.get(`api/relatorios/feedback/exportar?${p}`, { responseType: "blob" })

  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement("a")
  a.href = url
  a.download = `feedbacks-novo-diagweb_${f.dataInicio}_${f.dataFim}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
